import {
  ArrayPath,
  Control,
  FieldArray,
  FieldError,
  FieldErrors,
  FieldValues,
  Path,
  useFieldArray,
  UseFormRegister,
} from 'react-hook-form';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { Button } from 'components/Form/Button';
import { Input } from 'components/Form/Input/Input/Input';

export type StringListRow = { value: string };

type StringListSectionProps<TFieldValues extends FieldValues> = {
  name: ArrayPath<TFieldValues>;
  control: Control<TFieldValues>;
  register: UseFormRegister<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  addLabel: string;
  removeLabel: string;
  addAnalyticsId: string;
  removeAnalyticsId: string;
  label?: string;
  emptyLabel?: string;
  /** When false, the last remaining row cannot be removed (the remove button is hidden). Defaults to true. */
  allowRemoveLast?: boolean;
};

export const StringListSection = <TFieldValues extends FieldValues>({
  name,
  control,
  register,
  errors,
  addLabel,
  removeLabel,
  addAnalyticsId,
  removeAnalyticsId,
  label,
  emptyLabel,
  allowRemoveLast = true,
}: StringListSectionProps<TFieldValues>) => {
  const { fields, append, remove } = useFieldArray({ control, name });

  const fieldArrayErrors = errors[name] as Array<{ value?: FieldError }> | undefined;
  const canRemove = allowRemoveLast || fields.length > 1;

  return (
    <div className="space-y-3">
      {label ? <h3 className="text-sm font-semibold text-gray-900">{label}</h3> : null}

      {fields.length === 0 && emptyLabel ? (
        <p className="py-2 text-sm text-gray-500">{emptyLabel}</p>
      ) : (
        fields.map((row, index) => (
          <div key={row.id} className="flex items-start gap-2">
            <div className="flex-1">
              <Input
                {...register(`${name}.${index}.value` as Path<TFieldValues>)}
                errors={fieldArrayErrors?.[index]?.value}
              />
            </div>
            {canRemove ? (
              <Button
                type="button"
                variant="text"
                onClick={() => remove(index)}
                className="mt-2.5 text-gray-400! hover:text-gray-600!"
                aria-label={removeLabel}
                LeftIcon={XMarkIcon}
                analyticsId={removeAnalyticsId}
                iconOnly
              />
            ) : null}
          </div>
        ))
      )}

      <Button
        type="button"
        variant="secondary"
        size="small"
        onClick={() => append({ value: '' } as FieldArray<TFieldValues, typeof name>)}
        analyticsId={addAnalyticsId}
        LeftIcon={PlusIcon}
      >
        {addLabel}
      </Button>
    </div>
  );
};
